import { useRef, useCallback } from 'react';

/**
 * Плеер-агностичный хук для отслеживания просмотра контента.
 * Не знает ничего о Kodik или любом другом плеере.
 *
 * Использование:
 *   const { reportProgress, reportEpisodeChange } = useWatchTracker({
 *     materialId,
 *     enabled: isAuthenticated,
 *     threshold: 0.5,
 *     onWatched: ({ materialId, episode }) => addToHistory({ materialId, episode }),
 *   });
 *
 * Для Kodik — передавать данные из postMessage.
 * Для собственного плеера — передавать currentTime / duration напрямую.
 *
 * @param {Object}   params
 * @param {string}   params.materialId  - UUID материала
 * @param {boolean}  params.enabled     - активен ли трекинг (только для авторизованных)
 * @param {number}   [params.threshold] - порог просмотра (0–1), по умолчанию 0.5 (50%)
 * @param {Function} params.onWatched   - колбэк при достижении порога: ({ materialId, episode })
 */
export function useWatchTracker({ materialId, enabled, threshold = 0.5, onWatched }) {
  const currentEpisodeRef = useRef(0);
  const thresholdReachedRef = useRef(false);

  /**
   * Вызывается при каждом обновлении прогресса воспроизведения.
   * @param {number} time     - текущее время в секундах
   * @param {number} duration - общая длительность в секундах
   */
  const reportProgress = useCallback((time, duration) => {
    if (!enabled || !duration || duration <= 0) return;
    if (thresholdReachedRef.current) return;
    if (time / duration >= threshold) {
      thresholdReachedRef.current = true;
      onWatched({ materialId, episode: currentEpisodeRef.current });
    }
  }, [enabled, threshold, materialId, onWatched]);

  /**
   * Вызывается при смене серии.
   * Сбрасывает флаг порога для нового эпизода.
   * @param {number} episode - номер нового эпизода
   */
  const reportEpisodeChange = useCallback((episode) => {
    currentEpisodeRef.current = episode;
    thresholdReachedRef.current = false;
  }, []);

  return { reportProgress, reportEpisodeChange };
}
